import { useEffect, useMemo, useRef } from 'react';
import { useWatch, type Control, type UseFormSetValue } from 'react-hook-form';
import type { SchemaRule } from '@tasama/types';
import { resolveFieldState } from './conditions';
import {
  isComputeRule,
  isFieldStateRule,
  isOptionsByRule,
} from './rule-guards';
import { evaluateComputeRule } from './rule-evaluation.util';

interface UseDynamicFormRulesArgs {
  control: Control<Record<string, unknown>>;
  setValue: UseFormSetValue<Record<string, unknown>>;
  rules: SchemaRule[];
}

// Applies every FE-owned side effect from the contract that isn't pure render-time state:
// §5 COMPUTE (formula/COPY), §2 optionsBy's "clear if no longer valid", and the DEFAULT
// value that resolveFieldState computes but nothing previously consumed. Re-runs on every
// relevant value change; returns the per-field choices override for §2 rendering.
export function useDynamicFormRules({
  control,
  setValue,
  rules,
}: UseDynamicFormRulesArgs) {
  const values = useWatch({ control }) as Record<string, unknown>;
  // Tracks which (field, value) DEFAULT has already been written, so re-runs don't clobber
  // a value the user cleared back out after the default fired once.
  const appliedDefaults = useRef(new Set<string>());
  // Tracks the last seen *source* (`rule.from`) values per compute field, so this effect only
  // re-fires when a source field actually changes — not on every watch tick (e.g. the user
  // typing directly into the copy/compute target itself, which would otherwise get its
  // keystrokes immediately reverted back to the source's value).
  const lastComputeInputs = useRef<Record<string, string>>({});

  const computeRules = useMemo(() => rules.filter(isComputeRule), [rules]);
  const optionsByRules = useMemo(() => rules.filter(isOptionsByRule), [rules]);
  const staticRules = useMemo(() => rules.filter(isFieldStateRule), [rules]);

  useEffect(() => {
    computeRules.forEach((rule) => {
      const inputsKey = JSON.stringify(rule.from.map((name) => values[name]));
      if (lastComputeInputs.current[rule.field] === inputsKey) return;
      lastComputeInputs.current[rule.field] = inputsKey;

      const result = evaluateComputeRule(rule, values);
      if (result === undefined || values[rule.field] === result) return;
      // Every dynamic non-checkbox/non-file field is modeled as z.string() in
      // buildDynamicFormSchema — DATE_DIFF_DAYS returns a number, so it must be
      // stringified or it fails the schema's type check and blocks isValid entirely.
      setValue(rule.field, String(result), {
        shouldDirty: true,
        shouldValidate: true,
      });
    });
  }, [computeRules, setValue, values]);

  useEffect(() => {
    optionsByRules.forEach((rule) => {
      const drivingValue = values[rule.optionsBy.field];
      const choices =
        drivingValue != null
          ? rule.optionsBy.map[String(drivingValue)]
          : undefined;
      const currentAnswer = values[rule.field];
      const stillValid =
        currentAnswer == null ||
        currentAnswer === '' ||
        (choices ?? []).some((c) => c.value === currentAnswer);
      if (!stillValid) {
        setValue(rule.field, '', { shouldDirty: true, shouldValidate: true });
      }
    });
  }, [optionsByRules, setValue, values]);

  useEffect(() => {
    const defaultFields = new Set(
      staticRules
        .filter((rule) => rule.effect === 'DEFAULT')
        .map((rule) => rule.field)
    );
    defaultFields.forEach((fieldName) => {
      const state = resolveFieldState(fieldName, rules, values);
      if (state.defaultValue === undefined) return;
      const key = `${fieldName}:${String(state.defaultValue)}`;
      if (
        !appliedDefaults.current.has(key) &&
        !(values[fieldName] as string | number | boolean | undefined)
      ) {
        appliedDefaults.current.add(key);
        setValue(fieldName, state.defaultValue, { shouldDirty: false });
      }
    });
  }, [rules, staticRules, setValue, values]);

  const choicesOverride = useMemo(() => {
    const overrides: Record<string, { value: string; label: string }[]> = {};
    optionsByRules.forEach((rule) => {
      const drivingValue = values[rule.optionsBy.field];
      const choices =
        drivingValue != null
          ? rule.optionsBy.map[String(drivingValue)]
          : undefined;
      if (choices) overrides[rule.field] = choices;
    });
    return overrides;
  }, [optionsByRules, values]);

  return { choicesOverride };
}
